import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { idsToDelete, type DuplicateGroup } from "@/lib/masterDuplicates";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: "supplier_master" | "customer_master";
  label: string;
  groups: DuplicateGroup[];
  onDone: () => void;
}

const MasterDuplicatesDialog = ({ open, onOpenChange, table, label, groups, onDone }: Props) => {
  const { toast } = useToast();
  const [keep, setKeep] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { if (open) setKeep({}); }, [open]);

  const totalToDelete = useMemo(() => idsToDelete(groups, keep).length, [groups, keep]);

  const remove = async (target: DuplicateGroup[], key: string) => {
    const ids = idsToDelete(target, keep);
    if (!ids.length) return;
    const ok = window.confirm(`Se eliminarán ${ids.length} ficha(s) antigua(s). Esta acción no se puede deshacer. ¿Continuar?`);
    if (!ok) return;
    setBusy(key);
    let failed = 0;
    for (let i = 0; i < ids.length; i += 200) {
      const { error } = await supabase.from(table).delete().in("id", ids.slice(i, i + 200));
      if (error) failed++;
    }
    setBusy(null);
    if (failed) toast({ title: "Algunas fichas no se pudieron eliminar", variant: "destructive" });
    else toast({ title: `${ids.length} ficha(s) duplicada(s) eliminada(s)` });
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Duplicados de {label} por NIF</DialogTitle>
          <DialogDescription>
            Marca la cuenta que quieres conservar en cada caso. Por defecto se propone la subcuenta más alta (la más reciente).
          </DialogDescription>
        </DialogHeader>

        {groups.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No hay NIF con más de una cuenta.</p>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const keepId = keep[g.nif] ?? g.keepId;
              return (
                <div key={g.nif} className="rounded-lg border p-3 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-sm font-semibold">{g.nif}</span>
                    <Button
                      size="sm" variant="outline" className="gap-2"
                      disabled={!!busy}
                      onClick={() => remove([g], g.nif)}
                    >
                      {busy === g.nif ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      Eliminar antiguas
                    </Button>
                  </div>
                  {g.entries.map((e) => (
                    <label key={e.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent/40">
                      <input
                        type="radio"
                        name={`keep-${g.nif}`}
                        checked={keepId === e.id}
                        onChange={() => setKeep((k) => ({ ...k, [g.nif]: e.id }))}
                      />
                      <span className="font-mono w-28 shrink-0">{e.account}</span>
                      <span className="flex-1 min-w-0 truncate">{e.description}</span>
                      {keepId === e.id
                        ? <Badge variant="secondary">Conservar</Badge>
                        : <Badge variant="destructive">Eliminar</Badge>}
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          {groups.length > 0 && (
            <Button variant="destructive" className="gap-2" disabled={!!busy} onClick={() => remove(groups, "all")}>
              {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar todas las antiguas ({totalToDelete})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MasterDuplicatesDialog;
